// @flow
/** @jsx jsx */

import * as React from 'react';
import { first, isEmpty, tail } from 'lodash';

import { Colors } from '@zapier/theme';
import { css, jsx } from '@emotion/core';

const border = `1px solid ${Colors.adamantium}`;

const tableCss = {
  borderCollapse: 'collapse',
  margin: 0,
};

const bodyRowCss = {
  ':not(:last-child)': {
    borderBottom: border,
  },
  ':hover': {
    backgroundColor: Colors.storm,
  },
};

const baseCellCss = {
  lineHeight: 1.5,
  padding: '0.5em 1em',
  boxSizing: 'border-box',
};

const bodyCellCss = ({ paddingHorizontal, paddingVertical }) =>
  css(baseCellCss, {
    color: Colors.steel,
    paddingLeft: paddingHorizontal,
    paddingRight: paddingHorizontal,
    paddingTop: paddingVertical,
    paddingBottom: paddingVertical,
  });

const headerCellCss = ({ paddingHorizontal, paddingVertical, width }) =>
  css(baseCellCss, {
    borderBottom: border,
    color: Colors.deepSpace,
    fontWeight: 400,
    paddingLeft: paddingHorizontal,
    paddingRight: paddingHorizontal,
    paddingTop: paddingVertical,
    paddingBottom: paddingVertical,
    width,
  });

type Props = {
  /**
   * Render dense cells horizontally.
   */
  isDenseHorizontally?: boolean,
  /**
   * Render dense cells vertically.
   */
  isDenseVertically?: boolean,
  /**
   * Render dense cells horizontally and vertically. Supress isDenseHorizontally and
   * isDenseVertically if present.
   */
  isDense?: boolean,
  /**
   * The data to render as a table. It is an array which includes a header row
   * and one or multiple body rows. Each header/body row is also an array, with
   * elements to be table cells.
   */
  data: Array<Array<React.Node>>,
  /**
   * The widths of each columns.
   */
  columnWidths?: Array<number | string>,
};

/**
 * This component renders tabular data plainly in a simple table.
 */
const Table = (props: Props) => {
  const {
    columnWidths,
    data,
    isDense,
    isDenseHorizontally,
    isDenseVertically,
  } = props;

  const headerData = first(data);
  const bodyData = tail(data);

  const cellPaddingHorizontal = isDense || isDenseHorizontally ? 0 : undefined;
  const cellPaddingVertical = isDense || isDenseVertically ? 0 : undefined;

  return (
    (!isEmpty(headerData) || !isEmpty(bodyData)) && (
      <table css={tableCss}>
        <thead>
          {!isEmpty(headerData) && (
            <tr css={bodyRowCss} key="header">
              {headerData.map((cell, i) => (
                <th
                  css={headerCellCss({
                    paddingHorizontal: cellPaddingHorizontal,
                    paddingVertical: cellPaddingVertical,
                    width: columnWidths?.[i],
                  })}
                  key={i}
                >
                  {cell}
                </th>
              ))}
            </tr>
          )}
        </thead>
        <tbody>
          {!isEmpty(bodyData) &&
            bodyData.map((row, i) => (
              <tr css={bodyRowCss} key={i} row={row}>
                {row.map((cell, j) => (
                  <td
                    css={bodyCellCss({
                      paddingHorizontal: cellPaddingHorizontal,
                      paddingVertical: cellPaddingVertical,
                    })}
                    key={j}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
    )
  );
};

export default Table;
